import React, { FC, useEffect, useState, useCallback, useRef } from "react";
import { AxiosError } from "axios";
import { Button, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import useSpinnerLoader from "../../../../shared/components/spinner-loader";
import TableHeaderOrdering, {
  Header,
  OrderData,
} from "../../../../shared/components/table-header-ordering";
import { notificationService } from "../../../../shared/services/notification-service";
import TableRowNoData from "../../../../shared/components/molecules/commons/table-row-no-data";
import { HrOnlineService } from "../../../../shared/services/Report/HrOnline/hr-online";
import {
  Document,
  Page,
  View,
  Text,
  PDFDownloadLink,
  Font,
} from "@react-pdf/renderer";

import moment from "moment";
import PaginationForm from "../../../../shared/components/molecules/commons/pagination-form";
// Type definition for PDFDownloadLink render function
Font.register({
  family: "kanit-regular",
  src: "https://fonts.gstatic.com/s/kanit/v1/L6VKvM17ZmevDynOiw7H9w.ttf",
});
Font.register({
  family: "kanit-200",
  src: "https://fonts.gstatic.com/s/kanit/v1/wfLWkj1C4tYl7MoiFWS3bA.ttf",
});
interface PDFDownloadLinkRenderProps {
  blob: Blob | null;
  url: string | null;
  loading: boolean;
  error: Error | null;
}

const thaiMonths = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

const ReportRetirementTrendNext3YearsOTKByPositionListPage: FC = () => {
  const [queryParams, setQueryParams] = useState<any>({
    ordering_field: "",
    ordering: "",
    page: 1,
    page_size: 10,
    year: moment().year() + 543,
  });
  const [data, setData] = useState<any>([]);
  const [dataExport, setDataExport] = useState<any>([]);
  const { loaderComponent, startLoading, stopLoading } = useSpinnerLoader();
  const loadingRef = useRef({ startLoading, stopLoading });
  loadingRef.current = { startLoading, stopLoading };

  const headers: Header[] = [
    {
      label: "ลำดับ",
      field: "index",
      order: null,
      isOrder: true,
    },
    {
      label: "ตำแหน่ง",
      field: "type",
      order: null,
      isOrder: true,
    },
    {
      label: "กรอบ",
      field: "allocated_power_rate",
      order: null,
      isOrder: true,
    },
    {
      label: "บรรจุจริง",
      field: "vacant_positions",
      order: null,
      isOrder: true,
    },
    {
      label: "อัตราว่าง",
      field: "actual_filled",
      order: null,
      isOrder: true,
    },
    {
      label: (moment().year() + 543).toString(),
      field: "retire_current",
      order: null,
      isOrder: true,
    },
    {
      label: String(moment().year() + 544),
      field: "retire_next1",
      order: null,
      isOrder: true,
    },
    {
      label: String(moment().year() + 545),
      field: "retire_next2",
      order: null,
      isOrder: true,
    },
  ];

  const fetchData = useCallback(
    async (params: any) => {
      loadingRef.current.startLoading();
      try {
        const result =
          await HrOnlineService.getReportRetirementTrendNext3YearsOTKByPositionList(
            params
          );
        const resultAll =
          await HrOnlineService.getReportRetirementTrendNext3YearsOTKByPositionList(
            {
              ...params,
              page_size: 10000,
              page: 1,
            }
          );
        setData(result);
        setDataExport(resultAll);
      } catch (error) {
        const err = error as AxiosError;
        notificationService.dangerNotification(
          "โหลดข้อมูลไม่สำเร็จ",
          err.message
        );
      } finally {
        loadingRef.current.stopLoading();
      }
    },
    [queryParams.ordering, queryParams.page_size, queryParams.page]
  );

  const tableRowNoData = () => {
    if (data.rows?.employee?.length === 0) {
      return <TableRowNoData colspan={7} />;
    }
    return null;
  };

  const onChangePageSize = (page_size: number) => {
    setQueryParams({
      ...queryParams,
      page_size: page_size,
    });
  };

  const onChangePage = (page: number) => {
    setQueryParams({
      ...queryParams,
      page: page,
    });
  };

  const PDF: React.FC<{ data: any }> = ({ data }) => (
    <Document>
      <Page size="A4" style={{ padding: 20, fontFamily: "kanit-regular" }}>
        <View>
          {/* หัวรายงาน */}
          <View style={{ marginBottom: 20 }}>
            <Text
              style={{
                fontSize: 13,
                textAlign: "center",
                marginBottom: 3,
              }}
            >
              องค์การตลาดเพื่อเกษตรกร
            </Text>

            <Text
              style={{
                fontSize: 13,
                textAlign: "center",
                marginBottom: 3,
              }}
            >
              รายงานแนวโน้มการเกษียณอายุในปีถัดไป 3 ปี อ.ต.ก. เฉพาะระดับ 4 - 7
            </Text>

            <Text
              style={{
                fontSize: 13,
                textAlign: "center",
                marginBottom: 10,
              }}
            >
              ประจำวันที่{" "}
              {moment().format("D") +
                " " +
                thaiMonths[moment().month()] +
                " " +
                (moment().year() + 543)}
            </Text>
          </View>

          <View style={{ marginBottom: 10 }} fixed>
            {/* หัวตารางใหม่ตาม Table component */}
            <View
              style={{
                flexDirection: "row",
                padding: 5,
                borderBottomWidth: 1,
                borderTopWidth: 1,
              }}
            >
              <Text style={{ width: "5%", fontSize: 10, textAlign: "center" }}>
                ลำดับ
              </Text>
              <Text style={{ width: "25%", fontSize: 10, textAlign: "center" }}>
                ตำแหน่ง
              </Text>
              <Text style={{ width: "10%", fontSize: 10, textAlign: "center" }}>
                กรอบ
              </Text>
              <Text style={{ width: "10%", fontSize: 10, textAlign: "center" }}>
                บรรจุจริง
              </Text>
              <Text style={{ width: "10%", fontSize: 10, textAlign: "center" }}>
                อัตราว่าง
              </Text>
              <Text style={{ width: "12%", fontSize: 10, textAlign: "center" }}>
                {moment().year() + 543}
              </Text>
              <Text style={{ width: "12%", fontSize: 10, textAlign: "center" }}>
                {moment().year() + 544}
              </Text>
              <Text style={{ width: "16%", fontSize: 10, textAlign: "center" }}>
                {moment().year() + 545}
              </Text>
            </View>

            {data.rows?.position?.map((item: any, index: any) => (
              <View key={index} style={{ flexDirection: "row", padding: 3 }}>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "5%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {index + 1}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "25%",
                    fontSize: 8,
                    textAlign: "center",
                    paddingLeft: 1,
                  }}
                >
                  {item.structure_position_name_level}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "10%",
                    fontSize: 8,
                    textAlign: "left",
                  }}
                >
                  {item.allocated_power_rate}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "10%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item.vacant_positions}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "10%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item.actual_filled}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "12%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item.retire_current}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "12%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item.retire_next1}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "16%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item.retire_next2}
                </Text>
              </View>
            ))}

            {/* รวม */}
            {data.rows?.total_all?.map((item: any, index: any) => (
              <View key={index} style={{ flexDirection: "row", padding: 3 }}>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "5%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                ></Text>
                <Text
                  style={{
                    width: "25%",
                    fontSize: 8,
                    textAlign: "left",
                    paddingLeft: 1,
                  }}
                >
                  รวม
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "10%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item.allocated_power_rate}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "10%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item.vacant_positions}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "10%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item.actual_filled}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "12%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item.retire_current}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "12%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item.retire_next1}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "16%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {item.retire_next2}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );

  useEffect(() => {
    fetchData(queryParams);
  }, [fetchData, queryParams]);

  return (
    <>
      <div className="d-flex justify-content-between py-4">
        <h2 className="text-mof-primary m-0">
        รายงานแนวโน้มการเกษียณอายุในปีถัดไป 3 ปี อ.ต.ก. เฉพาะระดับ 4 - 7
        </h2>
      </div>
      {loaderComponent}
      <div className="row">
        {/* <label className="form-label">ปี</label>
        <div className="col-sm-12 col-md-2">
          <input
            className="form-control"
            type="text"
            value={queryParams.year ? queryParams.year : ""}
            onChange={handleYearChange}
            name="search"
          />
        </div>
        <div className="col-sm-12 col-md-4">
          <Button
            variant="primary"
            type="button"
            className="d-inline-block px-4"
            onClick={() => fetchData(queryParams)}
          >
            <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
          </Button>
        </div> */}
        <div className="col-sm-12 col-md-12 d-flex justify-content-end">
          <PDFDownloadLink
            document={<PDF data={dataExport} />}
            fileName={`รายงานแนวโน้มการเกษียณอายุในปีถัดไป 3 ปี อ.ต.ก. เฉพาะระดับ 4 - 7${queryParams.year}.pdf`}
          >
            {(({ loading }: any) => (
              <Button
                variant="primary"
                className="btn-action btn-mof-primary me-2"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faFileExport} className="me-2" />
                {loading ? "Loading..." : "Export"}
              </Button>
            )) as any}
          </PDFDownloadLink>
        </div>
      </div>

      <div className="overflow-auto mt-3">
        <Table striped bordered hover>
          <thead>
            <TableHeaderOrdering
              headers={headers}
              onChangeOrder={(orderData: OrderData) => {
                setQueryParams({
                  ...queryParams,
                  ordering_field: orderData.field,
                  ordering: orderData.order,
                });
              }}
              isShow={true}
            />
          </thead>
          <tbody>
            {data.rows?.position?.map((data: any, index: any) => {
              return (
                <ReportRetirementTrendNext3YearsBYPositionRow
                  key={index}
                  {...data}
                  index={index + 1}
                  type={`${data.structure_position_name_level}`}
                />
              );
            })}

            {tableRowNoData()}
          </tbody>
        </Table>
      </div>
      <div className="">
        <PaginationForm
          current_page={queryParams.page}
          count={data.count}
          page_size={queryParams.page_size}
          onChangePageSize={onChangePageSize}
          onChangePage={onChangePage}
        />
      </div>
    </>
  );
};

const ReportRetirementTrendNext3YearsBYPositionRow: FC<any> = ({
  index,
  main_power_rate,
  allocated_power_rate,
  retire_current,
  retire_next1,
  retire_next2,
  type = null,
}) => {
  return (
    <tr>
      <td>{type === "รวม" ? "" : index}</td>
      <td>{type}</td>
      <td>{main_power_rate}</td>
      <td>{allocated_power_rate}</td>
      <td>{main_power_rate - allocated_power_rate}</td>
      <td>{retire_current}</td>
      <td>{retire_next1}</td>
      <td>{retire_next2}</td>
    </tr>
  );
};

export default ReportRetirementTrendNext3YearsOTKByPositionListPage;
